import type { TaskDto } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  TASK_PRIORITY_LABELS,
  TASK_STATUS_LABELS,
  formatDate,
  formatDateTime,
  priorityTone,
  relativeDays,
  statusTone,
} from '../lib/format';
import { AttachmentList, AttachmentPicker, toRefs, type AttachmentChoice } from '../components/Attachments';
import { useConfirm } from '../components/ConfirmDialog';
import { TaskDialog } from '../components/TaskDialog';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorBlock,
  Input,
  LoadingBlock,
  Select,
  SectionTitle,
  Textarea,
  cn,
  useToast,
} from '../components/ui';

export function TaskDetailPage(): JSX.Element {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const { can } = useAuth();

  const [comment, setComment] = useState('');
  const [newItem, setNewItem] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [newAttachments, setNewAttachments] = useState<AttachmentChoice[]>([]);

  const { data: task, isLoading, error } = useQuery({
    queryKey: ['tasks', 'detail', id],
    queryFn: () => api.get<TaskDto>(`/api/tasks/${id}`),
    enabled: !!id,
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['tasks'] });
    await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const changeStatus = useMutation({
    mutationFn: (status: string) => api.patch<TaskDto>(`/api/tasks/${id}`, { status }),
    onSuccess: async () => {
      await invalidate();
      toast.show('Status geändert');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Änderung fehlgeschlagen', 'error'),
  });

  const addComment = useMutation({
    mutationFn: () => api.post<TaskDto>(`/api/tasks/${id}/comments`, { body: comment }),
    onSuccess: async () => {
      setComment('');
      await invalidate();
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Kommentar fehlgeschlagen', 'error'),
  });

  const addItem = useMutation({
    mutationFn: () => api.post<TaskDto>(`/api/tasks/${id}/checklist`, { title: newItem }),
    onSuccess: async () => {
      setNewItem('');
      await invalidate();
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Punkt konnte nicht hinzugefügt werden', 'error'),
  });

  const toggleItem = useMutation({
    mutationFn: ({ itemId, isDone }: { itemId: string; isDone: boolean }) =>
      api.patch<TaskDto>(`/api/tasks/${id}/checklist/${itemId}`, { isDone }),
    onSuccess: invalidate,
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Punkt konnte nicht geändert werden', 'error'),
  });

  const removeItem = useMutation({
    mutationFn: (itemId: string) => api.del(`/api/tasks/${id}/checklist/${itemId}`),
    onSuccess: async () => {
      await invalidate();
      toast.show('Punkt entfernt');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Punkt konnte nicht entfernt werden', 'error'),
  });

  const attach = useMutation({
    mutationFn: () => api.patch<TaskDto>(`/api/tasks/${id}`, {
      attachments: [
        ...(task?.attachments ?? []).map((a) => ({ kind: a.kind, refId: a.refId })),
        ...toRefs(newAttachments),
      ],
    }),
    onSuccess: async () => {
      await invalidate();
      setAttachOpen(false);
      setNewAttachments([]);
      toast.show('Anhänge gespeichert');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Anhängen fehlgeschlagen', 'error'),
  });

  const detach = useMutation({
    mutationFn: (attachmentId: string) => api.del<TaskDto>(`/api/tasks/${id}/attachments/${attachmentId}`),
    onSuccess: async () => {
      await invalidate();
      toast.show('Anhang entfernt');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Entfernen fehlgeschlagen', 'error'),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/api/tasks/${id}`),
    onSuccess: async () => {
      await invalidate();
      toast.show('Aufgabe gelöscht');
      navigate('/aufgaben');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Löschen fehlgeschlagen', 'error'),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;
  if (!task) return <ErrorBlock error={new Error('Aufgabe nicht gefunden')} />;

  const mayUpdate = task.canUpdate && can('tasks', 'update');
  const mayDelete = task.canDelete && can('tasks', 'delete');
  const mayChangeStatus = task.canChangeStatus;
  const mayComment = task.canComment;
  const doneItems = task.checklist.filter((c) => c.isDone).length;

  return (
    <div className="space-y-4">
      {confirmDialog}
      <Link to="/aufgaben" className="text-sm text-brand-700 hover:underline">
        ← Zurück zu den Aufgaben
      </Link>

      <Card>
        <CardBody className="space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <h1 className="text-xl font-semibold text-slate-900">{task.title}</h1>
            {(mayUpdate || mayDelete) && <div className="flex gap-2">
              {mayUpdate && (
              <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
                Bearbeiten
              </Button>
              )}
              {mayDelete && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  if (window.confirm('Aufgabe wirklich löschen?')) remove.mutate();
                }}
                loading={remove.isPending}
              >
                Löschen
              </Button>
              )}
            </div>}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={statusTone(task.status)}>{TASK_STATUS_LABELS[task.status]}</Badge>
            <Badge tone={priorityTone(task.priority)}>
              {TASK_PRIORITY_LABELS[task.priority]}
            </Badge>
            {task.dueAt && (
              <Badge tone={task.isOverdue ? 'danger' : 'neutral'}>
                Fällig {formatDate(task.dueAt)} ({relativeDays(task.dueAt)})
              </Badge>
            )}
            {task.recurrence !== 'NONE' && <Badge tone="info">Wiederkehrend</Badge>}
          </div>

          {task.description && (
            <p className="whitespace-pre-wrap text-sm text-slate-700">{task.description}</p>
          )}

          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-400">Bereiche</dt>
              <dd className="text-slate-700">
                {task.organisationUnits.map((u) => u.name).join(', ')}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-400">Verantwortlich</dt>
              <dd className="text-slate-700">
                {task.assignees.length > 0
                  ? task.assignees.map((a) => a.name).join(', ')
                  : 'Niemand zugewiesen'}
              </dd>
            </div>
            {task.tags.length > 0 && (
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-400">Schlagwörter</dt>
                <dd className="flex flex-wrap gap-1">
                  {task.tags.map((tag) => (
                    <Badge key={tag}>{tag}</Badge>
                  ))}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-400">Erstellt</dt>
              <dd className="text-slate-700">
                {formatDateTime(task.createdAt)}
                {task.createdByName ? ` von ${task.createdByName}` : ''}
              </dd>
            </div>
          </dl>

          {mayChangeStatus && <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
            <span className="text-sm text-slate-600">Status ändern:</span>
            <Select
              value={task.status}
              onChange={(e) => changeStatus.mutate(e.target.value)}
              className="w-auto"
              disabled={changeStatus.isPending}
            >
              {Object.entries(TASK_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            {task.blockedBy > 0 && (
              <Badge tone="warning">Wartet auf {task.blockedBy} andere Aufgabe(n)</Badge>
            )}
          </div>}
        </CardBody>
      </Card>

      {task.dependsOn.length > 0 && (
        <Card>
          <CardBody>
            <SectionTitle title="Hängt ab von" />
            <ul className="space-y-1">
              {task.dependsOn.map((dep) => (
                <li key={dep.id}>
                  <Link
                    to={`/aufgaben/${dep.id}`}
                    className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50"
                  >
                    <span className="truncate">{dep.title}</span>
                    <Badge tone={statusTone(dep.status)}>{TASK_STATUS_LABELS[dep.status]}</Badge>
                  </Link>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {(task.attachments.length > 0 || mayUpdate) && (
        <Card>
          <CardBody>
            <SectionTitle
              title="Anhänge"
              subtitle={task.attachments.length > 0 ? `${task.attachments.length} Datei(en)` : undefined}
              action={mayUpdate ? (
                <Button variant="secondary" size="sm" onClick={() => setAttachOpen(true)}>
                  + Anhang
                </Button>
              ) : undefined}
            />
            <AttachmentList
              attachments={task.attachments}
              removing={detach.isPending}
              onRemove={mayUpdate ? (attachment) => {
                const entry = task.attachments.find((a) => a.kind === attachment.kind && a.refId === attachment.refId);
                if (entry && window.confirm('Anhang von der Aufgabe entfernen?')) detach.mutate(entry.id);
              } : undefined}
            />
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody>
          <SectionTitle
            title="Checkliste"
            subtitle={
              task.checklist.length > 0 ? `${doneItems} von ${task.checklist.length} erledigt` : undefined
            }
          />
          <ul className="mb-3 space-y-1">
            {task.checklist.map((item) => (
              <li key={item.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={item.isDone}
                  onChange={(e) => toggleItem.mutate({ itemId: item.id, isDone: e.target.checked })}
                  disabled={!mayUpdate}
                  className="h-4 w-4 rounded border-slate-300 text-brand-600"
                />
                <span
                  className={cn('flex-1 text-sm', item.isDone && 'text-slate-400 line-through')}
                >
                  {item.title}
                </span>
                {mayUpdate && <button
                  type="button"
                  onClick={async () => {
                    const ok = await confirm({
                      title: 'Checklistenpunkt entfernen?',
                      message: `„${item.title}“ wird aus der Checkliste gelöscht.`,
                      confirmLabel: 'Ja, entfernen',
                      tone: 'danger',
                    });
                    if (ok) removeItem.mutate(item.id);
                  }}
                  className="text-xs text-slate-400 hover:text-rose-600"
                >
                  entfernen
                </button>}
              </li>
            ))}
          </ul>
          {mayUpdate && <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (newItem.trim()) addItem.mutate();
            }}
          >
            <Input
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              placeholder="Neuer Punkt …"
            />
            <Button type="submit" variant="secondary" loading={addItem.isPending}>
              Hinzufügen
            </Button>
          </form>}
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <SectionTitle title="Kommentare" />
          <ul className="mb-3 space-y-3">
            {(task.comments ?? []).map((c) => (
              <li key={c.id} className="rounded-lg bg-slate-50 px-3 py-2">
                <p className="text-sm text-slate-800">{c.body}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {c.personName} · {formatDateTime(c.createdAt)}
                </p>
              </li>
            ))}
            {(task.comments ?? []).length === 0 && (
              <li className="text-sm text-slate-500">Noch keine Kommentare.</li>
            )}
          </ul>
          {mayComment && <form
            className="space-y-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (comment.trim()) addComment.mutate();
            }}
          >
            <Textarea
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Kommentar schreiben …"
            />
            <Button type="submit" variant="secondary" loading={addComment.isPending}>
              Kommentieren
            </Button>
          </form>}
        </CardBody>
      </Card>

      {mayUpdate && <TaskDialog open={editOpen} onClose={() => setEditOpen(false)} task={task} />}
      {mayUpdate && (
        <Dialog
          open={attachOpen}
          onClose={() => setAttachOpen(false)}
          title="Dateien anhängen"
          footer={
            <>
              <Button variant="secondary" onClick={() => setAttachOpen(false)}>Abbrechen</Button>
              <Button onClick={() => attach.mutate()} loading={attach.isPending} disabled={newAttachments.length === 0}>
                Anhängen
              </Button>
            </>
          }
        >
          <AttachmentPicker
            value={newAttachments.filter((choice) =>
              !task.attachments.some((a) => a.kind === choice.kind && a.refId === choice.refId))}
            onChange={setNewAttachments}
          />
        </Dialog>
      )}
    </div>
  );
}
